import { useState } from "react";
import { Zap, Sparkles, Check } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useSidebar } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { useOrgQuota } from "@/hooks/useOrgQuota";

const plans = [
  {
    name: "Starter",
    price: "$49",
    period: "/mes",
    credits: "500 documentos",
    features: ["Procesamiento PDF", "1 usuario", "Soporte email"],
    highlighted: false,
  },
  {
    name: "Pro",
    price: "$149",
    period: "/mes",
    credits: "2,500 documentos",
    features: ["Todo en Starter", "5 usuarios", "API access", "Soporte prioritario"],
    highlighted: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    credits: "Ilimitado",
    features: ["Todo en Pro", "Usuarios ilimitados", "SLA 99.9%", "Soporte dedicado", "On-premise disponible"],
    highlighted: false,
  },
];

const PLAN_BADGE: Record<string, string> = {
  BASIC: "border-emerald-500/40 text-emerald-400 bg-emerald-500/10",
  PRO: "border-primary/40 text-primary bg-primary/10",
  ENTERPRISE: "border-purple-500/40 text-purple-400 bg-purple-500/10",
};

export function CreditUsage() {
  const quota = useOrgQuota();
  const [modalOpen, setModalOpen] = useState(false);
  const { state } = useSidebar();
  const collapsed = state === "collapsed";

  const isWarning = quota.pct >= 80;
  const isCritical = quota.pct >= 95;

  if (collapsed) {
    return (
      <div className="flex flex-col items-center gap-2 py-2">
        <Button variant="ghost" size="icon" onClick={() => setModalOpen(true)} className="text-primary hover:bg-primary/10">
          <Zap className="h-4 w-4" />
        </Button>
        <PricingModal open={modalOpen} onOpenChange={setModalOpen} />
      </div>
    );
  }

  return (
    <div className="space-y-2.5 px-3 py-2">
      <div className="rounded-lg p-3 space-y-2.5">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-medium text-muted-foreground">Documentos</span>
          <Badge
            variant="outline"
            className={cn("text-[9px] px-1.5 py-0", PLAN_BADGE[quota.planType] ?? PLAN_BADGE.BASIC)}
          >
            {quota.planLabel}
          </Badge>
        </div>
        <Progress
          value={quota.pct}
          className={cn(
            "h-1.5 bg-muted",
            isCritical
              ? "[&>div]:bg-destructive"
              : isWarning
                ? "[&>div]:bg-amber-500"
                : "[&>div]:bg-primary"
          )}
        />
        <div className="flex items-center justify-between">
          <span
            className={cn(
              "text-[11px] font-mono tabular-nums",
              isCritical ? "text-destructive" : isWarning ? "text-amber-400" : "text-primary"
            )}
          >
            {quota.used}/{quota.limit}
          </span>
          {isWarning && (
            <span className="text-[10px] text-amber-400">
              {isCritical ? "⛔ Límite" : "⚠ Cerca del límite"}
            </span>
          )}
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setModalOpen(true)}
        className="w-full text-xs gap-1.5 text-primary border-primary/30 hover:bg-primary/5"
      >
        <Sparkles className="h-3.5 w-3.5" />
        Upgrade Plan
      </Button>
      <PricingModal open={modalOpen} onOpenChange={setModalOpen} />
    </div>
  );
}

function PricingModal({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl glass border-border">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-foreground text-center">
            Escala tu operación con <span className="text-gradient-cyan">KargoNode</span>
          </DialogTitle>
          <p className="text-sm text-muted-foreground text-center">Elige el plan que mejor se adapte a tu volumen de comercio exterior</p>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={cn(
                "rounded-xl p-5 border transition-all",
                plan.highlighted
                  ? "border-primary/50 bg-primary/5 glow-cyan"
                  : "border-border bg-muted/20 hover:border-border/80"
              )}
            >
              {plan.highlighted && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-primary mb-2 block">Más Popular</span>
              )}
              <h4 className="text-lg font-bold text-foreground">{plan.name}</h4>
              <div className="mt-2 mb-4">
                <span className="text-3xl font-bold text-foreground">{plan.price}</span>
                <span className="text-sm text-muted-foreground">{plan.period}</span>
              </div>
              <p className="text-xs text-primary font-medium mb-4">{plan.credits}</p>
              <ul className="space-y-2 mb-5">
                {plan.features.map(f => (
                  <li key={f} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                size="sm"
                className={cn(
                  "w-full text-xs",
                  plan.highlighted
                    ? "bg-primary text-primary-foreground hover:bg-primary/90"
                    : "bg-muted text-foreground hover:bg-muted/80"
                )}
              >
                Seleccionar
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
